import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2,
  Plus,
  Search,
  Loader2,
  Edit,
  Save,
  X,
  Trash2,
  Cog,
  Users,
} from 'lucide-react';
import { filterItemsBySearch } from '../utils/textSanitize';

const emptyForm = () => ({
  nome: '',
  descricao: '',
  ordem: 0,
  equipamento_ids: [],
  usuario_ids: [],
});

const SetoresPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [usuariosProducao, setUsuariosProducao] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const empresaId = getEmpresaId();

  const load = async () => {
    if (!empresaId || empresaId === 'default') {
      setErr('Empresa não identificada. Selecione ou vincule uma empresa.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const [rSetores, rEq, rUsers] = await Promise.all([
        httpsCallable(functions, 'listSetores')({ empresa_id: empresaId, apenas_ativos: false }),
        httpsCallable(functions, 'listEquipamentos')({ empresa_id: empresaId }),
        httpsCallable(functions, 'listUsuariosProducaoSetor')({ empresa_id: empresaId }),
      ]);
      setItems(rSetores.data?.data?.items || []);
      setEquipamentos((rEq.data?.data?.items || []).filter((e) => e.status !== 'baixado'));
      setUsuariosProducao(rUsers.data?.data?.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar setores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [empresaId]);

  const filtered = useMemo(
    () => filterItemsBySearch(items, search, ['nome', 'descricao']),
    [items, search]
  );

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = async (row) => {
    setEditId(row.id);
    setForm({
      nome: row.nome || '',
      descricao: row.descricao || '',
      ordem: row.ordem || 0,
      equipamento_ids: row.equipamento_ids || [],
      usuario_ids: row.usuario_ids || [],
    });
    setModal(true);
  };

  const toggleId = (field, id) => {
    setForm((prev) => {
      const list = prev[field] || [];
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      return { ...prev, [field]: next };
    });
  };

  const save = async () => {
    if (!form.nome.trim()) {
      setErr('Informe o nome do setor.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const payload = {
        empresa_id: empresaId,
        nome: form.nome.trim(),
        descricao: form.descricao,
        ordem: Number(form.ordem) || 0,
        equipamento_ids: form.equipamento_ids,
        usuario_ids: form.usuario_ids,
      };
      if (editId) {
        await httpsCallable(functions, 'updateSetor')({ id: editId, ...payload });
        setMsg('Setor atualizado.');
      } else {
        await httpsCallable(functions, 'createSetor')(payload);
        setMsg('Setor criado.');
      }
      setModal(false);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (row) => {
    if (
      !window.confirm(
        `Excluir o setor "${row.nome}"?\n\nEquipamentos e usuários serão desvinculados.`
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    setErr('');
    try {
      await httpsCallable(functions, 'deleteSetor')({ id: row.id, empresa_id: empresaId });
      setMsg(`Setor "${row.nome}" excluído.`);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  const labelEquip = (id) => {
    const eq = equipamentos.find((e) => e.id === id);
    return eq ? eq.apelido || eq.tipo_nome || id : id;
  };

  const labelUser = (id) => {
    const u = usuariosProducao.find((x) => x.id === id);
    return u ? u.nome : id;
  };

  if (!hasPermission('manage_setores')) {
    return <div className="p-6 text-center text-gray-600">Sem permissão para gerenciar setores.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-600" />
            Setores da fábrica
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Organize a planta por setores e associe equipamentos e operadores de produção.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Novo setor
        </button>
      </div>

      {msg && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          {msg}
        </div>
      )}
      {err && (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
          placeholder="Buscar setor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((row) => (
            <div
              key={row.id}
              className={`bg-white border rounded-xl p-4 shadow-sm ${
                row.ativo === false ? 'opacity-60' : ''
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{row.nome}</h3>
                  {row.descricao && (
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{row.descricao}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(row)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {row.ativo !== false && (
                    <button
                      type="button"
                      onClick={() => excluir(row)}
                      disabled={deletingId === row.id}
                      className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                      title="Excluir"
                    >
                      {deletingId === row.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-600">
                <span className="flex items-center gap-1">
                  <Cog className="h-3.5 w-3.5" />
                  {row.total_equipamentos ?? row.equipamento_ids?.length ?? 0} equipamento(s)
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {row.total_usuarios ?? row.usuario_ids?.length ?? 0} operador(es)
                </span>
              </div>
              {(row.equipamento_ids?.length > 0 || row.usuario_ids?.length > 0) && (
                <div className="mt-2 text-xs text-gray-500 space-y-1">
                  {row.equipamento_ids?.slice(0, 3).map((id) => (
                    <div key={id}>• {labelEquip(id)}</div>
                  ))}
                  {row.equipamento_ids?.length > 3 && (
                    <div>… +{row.equipamento_ids.length - 3} equipamentos</div>
                  )}
                </div>
              )}
            </div>
          ))}
          {!filtered.length && (
            <p className="text-gray-500 col-span-full text-center py-8">
              Nenhum setor cadastrado. Clique em &quot;Novo setor&quot; para começar.
            </p>
          )}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 my-8 space-y-4">
            <h2 className="text-lg font-semibold">{editId ? 'Editar setor' : 'Novo setor'}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500">Nome *</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex.: Moagem, Embalagem, Câmara fria"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500">Descrição</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows={2}
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800 flex items-center gap-2 mb-2">
                <Cog className="h-4 w-4" /> Equipamentos
              </h3>
              {equipamentos.length === 0 ? (
                <p className="text-xs text-gray-500">Nenhum equipamento cadastrado na fábrica.</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {equipamentos.map((eq) => (
                    <label key={eq.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.equipamento_ids.includes(eq.id)}
                        onChange={() => toggleId('equipamento_ids', eq.id)}
                      />
                      <span>
                        {eq.apelido}{' '}
                        <span className="text-gray-400 text-xs">({eq.tipo_nome})</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-800 flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" /> Usuários — perfil Produção
              </h3>
              {usuariosProducao.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Nenhum usuário com perfil Produção nesta empresa. Cadastre em Usuários.
                </p>
              ) : (
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {usuariosProducao.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.usuario_ids.includes(u.id)}
                        onChange={() => toggleId('usuario_ids', u.id)}
                      />
                      <span>
                        {u.nome}{' '}
                        <span className="text-gray-400 text-xs">{u.email}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setModal(false)}
                className="px-4 py-2 border rounded-lg flex items-center gap-1"
              >
                <X className="h-4 w-4" /> Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetoresPage;
